
import React from 'react';
import { X, Calendar, Clock, Shield, AlertTriangle, Trash2, Key } from 'lucide-react';
import { OAuthClientDto } from '../../../../services/api';

interface ClientDetailModalProps {
  client: OAuthClientDto;
  onClose: () => void;
  onRevoke: (id: string) => void;
}

export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({ client, onClose, onRevoke }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRelativeTime = (dateString: string | undefined) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'hace unos segundos';
    if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
    return formatDate(dateString);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-end">
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-start justify-between bg-slate-50">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{client.name}</h2>
            <div className="flex gap-2 mt-2">
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                {client.type}
              </span>
              {client.isActive ? (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                  Activo
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                  Revocado
                </span>
              )}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {/* Client ID */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Identificador (Client ID)</label>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <Key className="text-slate-400" size={18} />
              <code className="text-sm font-mono text-slate-700 bg-transparent flex-1 select-all">
                {client.clientId}
              </code>
            </div>
          </div>

          {/* Description */}
          {client.description && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Descripción</label>
              <p className="text-slate-700 text-sm leading-relaxed bg-slate-50 p-4 rounded-lg border border-slate-100">
                {client.description}
              </p>
            </div>
          )}

          {/* Scopes */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-3">Permisos (Scopes)</label>
            <div className="space-y-2">
              {client.scopes.map((scope) => (
                <div key={scope} className="flex items-center gap-2 text-sm text-slate-700 p-2 rounded hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors">
                  <Shield size={16} className="text-green-600" />
                  <span>{scope}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Activity */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-3">Actividad</label>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Calendar size={16} className="text-slate-400" />
                <span>Creado el <span className="font-medium text-slate-900">{formatDate(client.createdAt)}</span></span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Clock size={16} className="text-slate-400" />
                <span>Último uso: <span className="font-medium text-slate-900">{getRelativeTime(client.lastUsedAt)}</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-6 border-t border-slate-100 bg-slate-50">
          {client.isActive ? (
             <div className="bg-red-50 border border-red-100 rounded-lg p-4">
               <h4 className="flex items-center gap-2 text-red-800 font-semibold text-sm mb-2">
                 <AlertTriangle size={16} />
                 Zona de Peligro
               </h4>
               <p className="text-red-600 text-xs mb-3">
                 Revocar el acceso invalidará el client secret actual y la aplicación dejará de funcionar inmediatamente.
               </p>
               <button
                onClick={() => onRevoke(client.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
               >
                 <Trash2 size={16} />
                 Revocar Acceso
               </button>
             </div>
          ) : (
            <div className="text-center py-2 text-slate-400 text-sm">
              Este cliente está inactivo/revocado.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
